import { enableProdMode, provideZoneChangeDetection, ErrorHandler, importProvidersFrom } from '@angular/core';
import * as Sentry from '@sentry/browser';

import { environment } from './environments/environment';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { SentryErrorHandler } from './app/error-handler';
import { HTTP_INTERCEPTORS, provideHttpClient, withXhr, withInterceptorsFromDi } from '@angular/common/http';
import { TimeoutInterceptor, DEFAULT_TIMEOUT } from './app/interceptor/timeout.interceptor';
import { UserbaseAuthStrategy, V1UserbaseAuthStrategy, AuthTokenPrefix, V1AuthTokenPrefix, RefreshTokenName, V1RefreshTokenName } from '@highfieldabc/angular-highfieldauth';
import { CORS_CONFIG, CredentialsInterceptor } from '@highfieldabc/angular-highfieldwebapi';
import { QuillModule } from 'ngx-quill';
import { provideDatabase } from './app/database/database.providers';
import { FSKDatabaseConfig, FSKPracticeDatabaseConfig } from './app/database/config/fsk-database-config';
import { provideLocalStoreRouting } from './app/guard/local-store.guard';
import { routes } from './app/app.routes';
import { AppComponent } from './app/app.component';

Sentry.init({
	dsn: 'https://37d8bba73539406596a0e02e8b6b6beb@o341742.ingest.sentry.io/5356011',
	integrations: [
		Sentry.browserTracingIntegration(),
	],
	tracesSampleRate: environment.sentryTraceRate,
	sampleRate: environment.sentryTraceRate,
	release: environment.sentryReleaseVersion,
	environment: environment.sentryEnvironment,
});

if (environment.production) {
	enableProdMode();
}

bootstrapApplication(AppComponent, {
	providers: [
		provideZoneChangeDetection(),
		provideRouter(routes, withInMemoryScrolling({ anchorScrolling: 'enabled' })),
		provideAnimations(),
		provideServiceWorker('ngsw-worker.js', { enabled: true, registrationStrategy: 'registerWhenStable:15000' }),
		importProvidersFrom(
			QuillModule.forRoot({
				modules: {
					toolbar: [
						['bold', 'italic', 'underline'], // toggled buttons
						[{ 'list': 'ordered' }, { 'list': 'bullet' }],
						[{ 'script': 'sub' }, { 'script': 'super' }], // superscript/subscript
						[{ 'indent': '-1' }, { 'indent': '+1' }], // outdent/indent
						[{ 'direction': 'rtl' }], // text direction
						[{ 'align': [] }],
						['clean'], // remove formatting button
						[{ 'header': [1, 2, 3, 4, 5, 6, false] }],
					]
				},
				customOptions: [{
					import: 'attributors/style/size',
					whitelist: ['8px', '10px', '12px', '14px', '16px', '18px', '20px']
				}]
			})
		),
		provideDatabase({ exam: FSKDatabaseConfig, practice: FSKPracticeDatabaseConfig }),
		provideLocalStoreRouting(),
		{ provide: ErrorHandler, useClass: SentryErrorHandler },
		{ provide: 'servicesBaseUrl', useValue: environment.servicesBaseUrl },
		{ provide: 'signalRBaseUrl', useValue: environment.signalRBaseUrl },
		{ provide: 'CDNUrl', useValue: environment.CDNUrl },
		{ provide: HTTP_INTERCEPTORS, useClass: TimeoutInterceptor, multi: true },
		{ provide: DEFAULT_TIMEOUT, useValue: 30000 },
		{ provide: 'dashboardUrl', useValue: environment.dashboardUrl },
		{ provide: 'markingUrl', useValue: environment.markingUrl },
		{ provide: 'QuestionBankSystemId', useValue: environment.questionBankSystemId },
		{ provide: 'CoreExamCDNCategoryId', useValue: environment.coreExamCDNCategoryId },
		{ provide: "CoreExamsUserId", useValue: environment.CoreExamsUserId },
		{ provide: "AuthApiBaseUrl", useValue: environment.AuthApiBaseUrl },
		{ provide: "LoginPageUrl", useValue: environment.LoginPageUrl },
		{ provide: "UserApiBaseUrl", useValue: environment.UserApiBaseUrl },
		{ provide: "LoginGuestPageUrl", useValue: environment.LoginPageUrl },
		{ provide: "RegisterPageUrl", useValue: environment.RegisterPageUrl },
		{ provide: "CDNApiBaseUrl", useValue: environment.CdnApiUrl },
		{ provide: UserbaseAuthStrategy, useClass: V1UserbaseAuthStrategy },
		{ provide: AuthTokenPrefix, useValue: V1AuthTokenPrefix },
		{ provide: RefreshTokenName, useValue: V1RefreshTokenName },
		{ provide: CORS_CONFIG, useValue: { withCredentials: environment.withCredentials } },
		{ provide: HTTP_INTERCEPTORS, useClass: CredentialsInterceptor, multi: true },
		provideHttpClient(withXhr(), withInterceptorsFromDi())
	]
})
	.catch(err => console.log(err));
